/**
 * ExitSurvey — asked BEFORE we hand a coach to Stripe's cancel flow.
 *
 * Cancellation itself happens in Stripe's billing portal, so this is the
 * last moment we control. Two jobs, in order:
 *
 *   1. Ask why. One tap, plus optional detail. This is the only place the
 *      reason can be captured — once they're in the portal they're gone
 *      and all we get back is a flag flipping.
 *   2. Offer the obvious save. Most cancellations aren't "this is bad",
 *      they're "not right now" — a pause beats a cancel for both sides,
 *      and "too expensive" is worth one conversation before it's final.
 *
 * Nothing here BLOCKS cancelling. The continue button is always live, the
 * reason is optional, and skipping still records that they left. A survey
 * that traps people is a survey that gets lied to.
 */
import React, { useState } from "react";
import { X, PauseCircle } from "lucide-react";
import { GOLD } from "../constants/theme";
import { supabase } from "../../supabaseClient";
import { assertNotStaging } from "../../config/environment";

// Coded buckets so the admin view can group them; the free-text box is
// where the actual insight tends to live.
const REASONS = [
  { id: "too_expensive",   label: "Too expensive" },
  { id: "not_using",       label: "Not using it enough" },
  { id: "missing_content", label: "Didn't have what I needed" },
  { id: "season_over",     label: "My season ended" },
  { id: "technical",       label: "Too many problems / bugs" },
  { id: "found_alt",       label: "Using something else" },
  { id: "other",           label: "Something else" },
];

export function ExitSurvey({ C, onClose, onContinue, onPaused }) {
  const [reason, setReason] = useState(null);
  const [detail, setDetail] = useState("");
  const [saving, setSaving] = useState(false);
  const [pausing, setPausing] = useState(false);
  const [pauseErr, setPauseErr] = useState(null);

  // Record the reason first, then pause. If the reason write fails we
  // still pause — keeping the coach matters more than the telemetry.
  const doPause = async (months) => {
    setPausing(true); setPauseErr(null);
    try {
      await record();
      assertNotStaging("Pausing a membership");
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const base = process.env.REACT_APP_SUPABASE_URL || "https://rlfekxctjyvozujzbzwm.supabase.co";
      const r = await fetch(`${base}/functions/v1/pause-subscription`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ months }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
 setPauseErr(j?.message || j?.error || "Couldn't pause right now: email support@byanymeanscoaches.com and we'll sort it.");
        return;
      }
      onPaused?.(j);
    } catch {
      setPauseErr("Couldn't reach the server. Check your connection and try again.");
    } finally {
      setPausing(false);
    }
  };

  const record = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return;
      await supabase.from("churn_events").insert({
        user_id: user.id,
        email: user.email,
        source: "survey",
        reason: reason || null,
        reason_detail: detail.trim() || null,
      });
    } catch { /* never block someone from cancelling over telemetry */ }
  };

  const proceed = async () => {
    setSaving(true);
    await record();
    onContinue();
  };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 10000, background: "rgba(0,0,0,0.72)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 18,
      backdropFilter: "blur(3px)",
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: "100%", maxWidth: 460, maxHeight: "90vh", overflowY: "auto",
        background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 16,
        padding: 22, fontFamily: "'DM Sans',sans-serif", position: "relative",
      }}>
        <button onClick={onClose} aria-label="Close" className="btn"
          style={{ position: "absolute", top: 12, right: 12, background: "transparent",
            border: "none", color: C.textDim, cursor: "pointer", padding: 6 }}>
          <X size={17}/>
        </button>

        <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: 24, letterSpacing: 1.6, color: C.text, marginBottom: 6 }}>
          BEFORE YOU GO
        </div>
        <div style={{ fontSize: 13, color: C.textMid, lineHeight: 1.6, marginBottom: 18 }}>
 What made you decide to cancel? One tap helps us more than you'd think: and it's optional.
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
          {REASONS.map((r) => (
            <button key={r.id} className="btn" onClick={() => setReason(reason === r.id ? null : r.id)}
              style={{
                padding: "9px 14px", borderRadius: 9, minHeight: 44, cursor: "pointer",
                fontSize: 12.5, fontWeight: 600, fontFamily: "inherit",
                background: reason === r.id ? GOLD : "transparent",
                color: reason === r.id ? "#111" : C.textMid,
                border: `1px solid ${reason === r.id ? GOLD : C.border}`,
              }}>
              {r.label}
            </button>
          ))}
        </div>

        <textarea value={detail} onChange={(e) => setDetail(e.target.value)}
 placeholder="Anything else? Be blunt: it's the most useful thing you can leave us with."
          rows={3}
          style={{
            width: "100%", padding: "11px 13px", borderRadius: 10, resize: "vertical",
            background: "rgba(255,255,255,0.03)", color: C.text,
            border: `1px solid ${C.border}`, fontSize: 13, fontFamily: "inherit",
            lineHeight: 1.5, marginBottom: 16, boxSizing: "border-box",
          }}/>

        {/* The save. Not a plea — a genuinely better option for a
            seasonal user: keep your price, keep your work, stop paying
            for the months you're not coaching. Offered to everyone,
            because plenty of people who'd cancel outright will take a
            pause when it's actually on the table. */}
        <div style={{
          padding: "14px 15px", borderRadius: 11, marginBottom: 16,
          background: "rgba(226,221,159,0.06)", border: `1px solid ${GOLD}33`,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
            <PauseCircle size={15} color={GOLD}/>
            <span style={{ fontSize: 13.5, fontWeight: 800, color: C.text }}>Pause instead?</span>
          </div>
          <div style={{ fontSize: 12.5, color: C.textMid, lineHeight: 1.55, marginBottom: 11 }}>
            {reason === "season_over"
 ? "Your season's done: pause and come back when it starts again. You won't be charged while you're paused, and your plans and saved drills are exactly where you left them."
              : "Stop the billing for a few months and pick up where you left off. Nothing is charged while paused, your price stays the same, and your plans and saved drills are waiting."}
          </div>
          {pauseErr && (
            <div style={{ fontSize: 12, color: "#F0B5A1", marginBottom: 9 }}>{pauseErr}</div>
          )}
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {[1, 2, 3].map((m) => (
              <button key={m} className="btn" disabled={pausing}
                onClick={() => doPause(m)}
                style={{
                  flex: "1 1 90px", padding: "10px 12px", borderRadius: 9, minHeight: 44,
                  cursor: pausing ? "default" : "pointer", fontFamily: "inherit",
                  background: GOLD, color: "#111", border: "none",
                  fontSize: 12.5, fontWeight: 800, opacity: pausing ? 0.5 : 1,
                }}>
                {pausing ? "…" : `${m} month${m === 1 ? "" : "s"}`}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: C.textDim, marginTop: 8, lineHeight: 1.5 }}>
 Access pauses too: it comes back automatically when your pause ends. One pause per year.
          </div>
        </div>

        <div style={{ display: "flex", gap: 9, flexWrap: "wrap" }}>
          <button className="btn" onClick={onClose}
            style={{ flex: "1 1 140px", padding: "12px 16px", borderRadius: 10, minHeight: 44,
              background: "transparent", color: C.text, border: `1px solid ${C.border}`,
              fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            Never mind, stay
          </button>
          <button className="btn" onClick={proceed} disabled={saving}
            style={{ flex: "1 1 140px", padding: "12px 16px", borderRadius: 10, minHeight: 44,
              background: "transparent", color: C.textDim, border: `1px solid ${C.border}`,
              fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
              opacity: saving ? 0.5 : 1 }}>
            {saving ? "One sec…" : "Continue to cancel"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ExitSurvey;
